/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo } from 'react';

import {
  Button,
  ButtonGroup,
  Form,
} from 'react-bootstrap';
// eslint-disable-next-line no-unused-vars
import _ from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { DEFAULT_TIMEZONE, formatDateTime as format, toUtcIsoString } from '../../Utils/timezone';

import fetchOrdersWrapper from '../Orders/Functions/fetchOrders';
import markAsCompletedWrapper from '../Orders/Functions/markAsCompleted';
import archiveOrderWrapper from '../Orders/Functions/archiveOrder';
import OrdersTable from '../Orders/Components/OrdersTable';
import DayTimeline from './Components/DayTimeline';
import OrderInputModal from '../Orders/Components/OrderInputModal';
import OrderDetailsModal from '../Orders/Components/OrderDetailsModal';
import OperatorInputModal from '../Orders/Components/OperatorInputModal';
import DriverInputModal from '../Orders/Components/DriverInputModal';
import useModalState from '../../Hooks/useModalState';
import useCsvExport from '../../Hooks/useCsvExport';
import Error from '../../Components/Error';
import useAskAiSuggestion from '../../Components/ChatWidget/askAiSuggestions';

dayjs.extend(utc);
dayjs.extend(tz);

/**
 * THE TIMELINE DRAWS THE WHOLE DAY, so it asks for the whole day in one
 * page. `/bookings` pages by 20 when it is not told otherwise, and with the
 * screen's newest-first sort the old timeline silently showed only the LAST
 * 20 trips of a busy day — its morning was simply missing.
 *
 * 200 is the most the API documents for `pageSize` (ph-core
 * Schemas/Zod/Bookings.schema.ts, `BookingsQueryParams`, the OpenAPI
 * contract; not enforced at runtime today, but this stays inside it). The
 * busiest real day so far was 94 trips, an F1 weekend. The Table keeps its
 * pager; the timeline's summary says "showing N of M" if a day ever
 * outgrows this.
 */
export const TIMELINE_PAGE_SIZE = 200;

function Trips() {
  const dateStartDefault = dayjs().tz(DEFAULT_TIMEZONE).startOf('day');
  const dateEndDefault = dayjs().tz(DEFAULT_TIMEZONE).endOf('day');

  /** The normal day view's filter set, for the day from `start` to `end`. */
  const dayFilters = (start, end) => ({
    page: 1,
    // Completed too, so the day's finished trips stay on the timeline.
    status: ['Confirmed', 'Completed'],
    date: [toUtcIsoString(start), toUtcIsoString(end)],
    sort: { date_datetime: 'desc' },
  });

  const [orders, setOrders] = useState([]);

  const orderDetailsModal = useModalState();
  const operatorInputModal = useModalState();
  const orderInputModal = useModalState();
  const driverInputModal = useModalState();
  const tripConfirmationModal = useModalState();
  const pickupGuideModal = useModalState();
  const addInvoiceModal = useModalState();
  // Which Order Details tab the timeline opens a booking on (the same
  // `initialTab` mechanism OrdersTable uses).
  const [detailsInitialTab, setDetailsInitialTab] = useState('overview');

  const [dateStart, setDateStart] = useState(dateStartDefault);
  const [dateEnd, setDateEnd] = useState(dateEndDefault);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalTrips, setTotalTrips] = useState(0);

  const [filters, setFilters] = useState(() => dayFilters(dateStartDefault, dateEndDefault));
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [quickSearch, setQuickSearch] = useState('');
  const [viewMode, setViewMode] = useState('timeline');

  /**
   * WHAT TO ASK ON THE DAILY OVERVIEW — the day's gap, for the day actually
   * on screen rather than for today.
   *
   * This is the registration that justifies pages pushing their own
   * suggestion instead of a central route map: `useLocation()` would only
   * ever say `/trips`, and the date is held in this component's state, not in
   * the URL. `search_bookings` reads a bare YYYY-MM-DD as Budapest-local
   * start/end of day, which is the same day this screen drew.
   *
   * Registered only once a day with trips in it has loaded — `useAskAiSuggestion`
   * takes null for "nothing yet", so an empty Sunday offers the global list
   * rather than an offer to audit nothing. The count is the gate and stays out
   * of the prompt text: it is the count AFTER the screen's quick search and
   * link filters, so a prompt quoting it could be quietly wrong.
   */
  useAskAiSuggestion(!isLoading && totalTrips > 0 ? {
    label: 'Ask about this day',
    question: `Which trips on ${dateStart.format('D MMM')} have no driver?`,
    prompt: `List the bookings with a pickup on ${dateStart.format('YYYY-MM-DD')} (Budapest time)`
      + ' that have no driver assigned yet — booking reference, pickup time, route and'
      + ' operator — earliest first, and say how many of the day\'s trips that is.',
  } : null);

  /**
   * DAILY OVERVIEW IS A TRIP LIST, SO IT LEAVES EXPENSES OUT.
   *
   * An expense has no route, no driver and no time anybody has to be
   * anywhere — it is a cost recorded against a past date. Dropped into this
   * day's timeline it reads as a job somebody has to do, which is the
   * confusion the booking type exists to end.
   *
   * Applied HERE, where the filter set is handed over, rather than in
   * `dayFilters`, for two reasons:
   *
   *   1. `setFilters` is called with a WHOLE new object in places — the
   *      timeline's link badge does `setFilters({ linked_booking_ids })` —
   *      so a default that lived only in the initial state would silently
   *      disappear the first time one of those ran.
   *   2. The CSV export shares this exact object (`useCsvExport` below), so
   *      the download matches the screen. Excluding inside `fetchOrders`
   *      instead would have left expenses in the export only.
   *
   * It is a DEFAULT, not a rule: `??` lets a caller that genuinely wants
   * expenses pass its own `exclude_booking_types`. The Orders screen builds
   * its filters separately and is untouched — that list SHOULD show them.
   */
  const effectiveFilters = useMemo(() => ({
    ...filters,
    exclude_booking_types: filters.exclude_booking_types ?? ['expense'],
  }), [filters]);

  const { exportCsv, isExporting } = useCsvExport('/bookings', effectiveFilters);

  /**
   * What each view reads. The timeline asks for its whole day in one page,
   * and for each booking's `estimated_end` — where its bar stops. Only the
   * timeline asks for that: the API looks flights up to answer it, which
   * neither the Table nor the CSV export (above, the bare filter set) has
   * any use for.
   */
  const isTimeline = viewMode === 'timeline';
  const request = useMemo(() => (isTimeline
    ? { ...effectiveFilters, page: 1, pageSize: TIMELINE_PAGE_SIZE }
    : effectiveFilters), [effectiveFilters, isTimeline]);

  const fetchOrders = () => fetchOrdersWrapper({
    setIsLoading,
    setOrders,
    setTotalPages,
    setError,
    setTotalOrders: setTotalTrips,
  })(request, isTimeline ? { with_estimated_end: true } : {});

  const markAsCompleted = (order) => markAsCompletedWrapper({
    setIsLoading,
    fetchOrders,
  })(order);

  const archiveOrder = (order) => archiveOrderWrapper({
    setIsLoading,
    fetchOrders,
    setError,
  })(order);

  const debouncedFetchOrders = _.debounce((appliedFilters) => {
    fetchOrders(appliedFilters);
  }, 500);

  const sortFn = (column, desc = true) => {
    setFilters({ ...filters, sort: { [column]: desc ? 'desc' : 'asc' }, page });
  };

  /**
   * CHOOSING A DAY GOES BACK TO THAT DAY'S NORMAL VIEW.
   *
   * The linked-bookings badge (the timeline's and the Table's) and the
   * Table's e-mail link REPLACE the filter set with their one search. The
   * day controls used to merge the new date into whatever was there, so that
   * search stuck to every day afterwards — with no status filter either —
   * until the page was reloaded. A day is rebuilt from `dayFilters` instead,
   * keeping only what the toolbar itself still shows: the search box's text,
   * which goes on narrowing the day as it always did, and the Table's sort,
   * whose column button keeps its own arrow. The Table starts again at page 1.
   *
   * Every caller moves to the day FIRST and only then takes its edges here.
   * Arithmetic on a timezone-bound dayjs keeps the UTC offset it started
   * with, so `.endOf('day').add(1, 'day')` took 23:59 at today's offset and
   * shifted it by exactly 24 hours. On the day before Budapest leaves summer
   * time that landed on 22:59, and every trip between 23:00 and 23:59 on the
   * 25th was missing from the screen; in March it ran the other way and
   * reached an hour into the day after. operatorFormat.js explains the same
   * trap for the portal, where `rangeFor` avoids it by working on YMD.
   */
  const showDay = (day) => {
    const start = day.startOf('day');
    const end = day.endOf('day');
    const search = quickSearch.trim();

    setDateStart(start);
    setDateEnd(end);
    setPage(1);
    setFilters((prev) => ({
      ...dayFilters(start, end),
      ...(prev.sort ? { sort: prev.sort } : {}),
      ...(search ? { quick_search: search } : {}),
    }));
  };

  const filterDateByYesterday = () => showDay(dayjs().tz(DEFAULT_TIMEZONE).subtract(1, 'day'));

  const filterDateByToday = () => showDay(dayjs().tz(DEFAULT_TIMEZONE));

  const filterDateByTomorrow = () => showDay(dayjs().tz(DEFAULT_TIMEZONE).add(1, 'day'));

  const handleDatePickerChange = (e) => showDay(dayjs(e.target.value).tz(DEFAULT_TIMEZONE, true));

  const activeQuickButton = useMemo(() => {
    const today = dayjs().tz(DEFAULT_TIMEZONE).startOf('day');
    const selectedDay = dateStart.startOf('day');

    if (selectedDay.isSame(today.subtract(1, 'day'), 'day')) return 'yesterday';
    if (selectedDay.isSame(today, 'day')) return 'today';
    if (selectedDay.isSame(today.add(1, 'day'), 'day')) return 'tomorrow';
    return null;
  }, [dateStart]);

  const selectedDateForPicker = useMemo(() => dateStart.format('YYYY-MM-DD'), [dateStart]);

  const handleQuickSearchChange = (e) => {
    const searchValue = e.target.value;
    setQuickSearch(searchValue);

    if (searchValue.trim()) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        quick_search: searchValue.trim(),
        page: 1,
      }));
    } else {
      setFilters((prevFilters) => {
        const { quick_search: removed, ...rest } = prevFilters;
        return { ...rest, page: 1 };
      });
    }
  };

  const clearQuickSearch = () => {
    setQuickSearch('');
    setFilters((prevFilters) => {
      const { quick_search: removed, ...rest } = prevFilters;
      return { ...rest, page: 1 };
    });
  };

  useEffect(() => {
    debouncedFetchOrders(request);
    return () => debouncedFetchOrders.cancel();
  }, [request]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page }));
  }, [page]);

  if (error) {
    return <Error message={error} />;
  }

  return (
    <>
      <h1 className="page-title">Daily Overview</h1>

      {/* Compact toolbar */}
      <div className="daily-toolbar">
        {/* Row 1: date nav + controls */}
        <div className="daily-toolbar-row">
          {/* Date label + quick buttons */}
          <div className="daily-date-section">
            <span className="daily-date-label">
              {format(dateStart, 'ddd, MMM D')}
            </span>
            <ButtonGroup size="sm">
              <Button
                variant={activeQuickButton === 'yesterday' ? 'primary' : 'outline-secondary'}
                onClick={filterDateByYesterday}
              >
                Yesterday
              </Button>
              <Button
                variant={activeQuickButton === 'today' ? 'primary' : 'outline-secondary'}
                onClick={filterDateByToday}
              >
                Today
              </Button>
              <Button
                variant={activeQuickButton === 'tomorrow' ? 'primary' : 'outline-secondary'}
                onClick={filterDateByTomorrow}
              >
                Tomorrow
              </Button>
            </ButtonGroup>
            <Form.Control
              type="date"
              size="sm"
              value={selectedDateForPicker}
              onChange={handleDatePickerChange}
              className="daily-date-picker"
            />
          </div>

          {/* Right controls */}
          <div className="daily-controls">
            {/* Trip count */}
            <span className="daily-trip-count">
              <i className="fa fa-car me-1" />
              {isLoading ? '…' : totalTrips}
              {' '}
              <span className="d-none d-sm-inline">
                trip
                {totalTrips !== 1 ? 's' : ''}
              </span>
            </span>

            {/* View toggle */}
            <ButtonGroup size="sm">
              <Button
                variant={viewMode === 'timeline' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('timeline')}
                title="Timeline view"
              >
                <i className="fa fa-stream" />
                <span className="d-none d-md-inline ms-1">Timeline</span>
              </Button>
              <Button
                variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('table')}
                title="Table view"
              >
                <i className="fa fa-table" />
                <span className="d-none d-md-inline ms-1">Table</span>
              </Button>
            </ButtonGroup>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={fetchOrders}
              title="Refresh"
            >
              <i className={`fa fa-sync ${isLoading ? 'fa-spin' : ''}`} />
            </Button>

            <Button
              size="sm"
              variant="outline-success"
              onClick={exportCsv}
              disabled={isExporting}
              title="Export CSV"
            >
              <i className={`fa fa-download ${isExporting ? 'fa-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Row 2: search */}
        <div className="daily-search-row">
          <div className="daily-search-wrap">
            <i className="fa fa-search daily-search-icon" />
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search by name, email, booking ref, flight number..."
              value={quickSearch}
              onChange={handleQuickSearchChange}
              className="daily-search-input"
            />
            {quickSearch && (
              <button
                type="button"
                className="daily-search-clear"
                onClick={clearQuickSearch}
                aria-label="Clear search"
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'timeline' ? (
        <DayTimeline
          trips={orders}
          isLoading={isLoading}
          total={totalTrips}
          // The days the screen is showing, so the timeline knows whether
          // "now" is even in them — a red now-line across next Tuesday
          // would be telling the office something untrue. The linked-bookings
          // search drops the date filter and shows no day at all: then the
          // timeline spans the bookings it found, and draws no now-line.
          dayFrom={filters.date ? dateStart : null}
          dayTo={filters.date ? dateEnd : null}
          onEditBooking={(trip) => orderInputModal.show(trip)}
          onAssignOperator={(trip) => operatorInputModal.show(trip)}
          onAssignDriver={(trip) => driverInputModal.show(trip)}
          onViewDetails={(trip) => { setDetailsInitialTab('overview'); orderDetailsModal.show(trip); }}
          onViewDriverEvents={(trip) => { setDetailsInitialTab('driver-events'); orderDetailsModal.show(trip); }}
          // The link badge, doing what the Orders table's "Show Linked
          // Bookings" does: REPLACE the filter set with just the link, so
          // every booking tied to this one shows whatever its date or
          // status. Without this prop the badge renders for nobody — the
          // timeline hides it unless a handler is passed.
          onSearchLinkedBookings={(trip) => setFilters({
            // eslint-disable-next-line no-underscore-dangle
            linked_booking_ids: trip._id,
          })}
        />
      ) : (
        <OrdersTable
          columns={{
            partner: true,
            partner_booking_reference: true,
            operator: true,
            operator_price: true,
            driver: true,
            customer_name: true,
            transfer_date: true,
            from: true,
            category: false,
            flight_number: true,
            pax: true,
            to: true,
            price: true,
            status: false,
            warnings: true,
            actions: { orders: true },
          }}
          orders={orders}
          page={page}
          totalPages={totalPages}
          setFilters={setFilters}
          archiveOrder={archiveOrder}
          isLoading={isLoading}
          fetchOrders={debouncedFetchOrders}
          markAsCompleted={markAsCompleted}
          setPage={setPage}
          orderInputModal={orderInputModal}
          orderDetailsModal={orderDetailsModal}
          operatorInputModal={operatorInputModal}
          driverInputModal={driverInputModal}
          tripConfirmationModal={tripConfirmationModal}
          pickupGuideModal={pickupGuideModal}
          addInvoiceModal={addInvoiceModal}
          sortFn={sortFn}
        />
      )}

      {viewMode === 'timeline' && (
        <>
          {orderDetailsModal.isVisible && (
            <OrderDetailsModal
              order={orderDetailsModal.selectedObject}
              show={orderDetailsModal.isVisible}
              handleClose={orderDetailsModal.hide}
              initialTab={detailsInitialTab}
            />
          )}
          {orderInputModal.isVisible && (
            <OrderInputModal
              originalOrder={orderInputModal.selectedObject}
              show={orderInputModal.isVisible}
              fetchOrders={fetchOrders}
              handleClose={orderInputModal.hide}
            />
          )}
          {operatorInputModal.isVisible && (
            <OperatorInputModal
              originalOrder={operatorInputModal.selectedObject}
              show={operatorInputModal.isVisible}
              handleClose={operatorInputModal.hide}
              fetchOrders={fetchOrders}
            />
          )}
          {driverInputModal.isVisible && (
            <DriverInputModal
              originalOrder={driverInputModal.selectedObject}
              show={driverInputModal.isVisible}
              handleClose={driverInputModal.hide}
              fetchOrders={fetchOrders}
            />
          )}
        </>
      )}
    </>
  );
}

export default Trips;
